import { Service, UploadFile } from '@anispi-reporting-system/types';
import { CheckCircleOutline, CloudUpload, Delete, ErrorOutline } from '@mui/icons-material';
import { Box, FormControl, Grid, IconButton, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import deepEqual from 'deep-equal';
import React, { FunctionComponent, memo } from 'react';
import { filterNumber } from '../../lib/filterNumber';
import { labels } from '../../lib/labels';
import { ErrorMessages } from '../ErrorMessages';

interface Props {
  file: UploadFile;
  handleChange: (fileKey: string, value: Partial<Pick<UploadFile, 'officeNo' | 'companyNo' | 'service'>>) => void;
  handleDelete: (key: string) => void;
}

// eslint-disable-next-line react/display-name
const UploadCsvFileProperties: FunctionComponent<Props> = memo(
  // eslint-disable-next-line react/prop-types
  ({ file, handleChange, handleDelete }) => {
    // eslint-disable-next-line react/prop-types
    const { check, companyNo, errorMessages, fileName, key, officeNo, service, sent } = file;

    return (
      <Grid container alignItems="center" justifyContent="center">
        <Grid item xs={4}>
          <Box className="flex flex-row space-x-2 pr-2">
            {check === undefined && <CheckCircleOutline color="disabled" />}
            {check === true && <CheckCircleOutline color="success" />}
            {check === false && <ErrorOutline color="error" />}
            {sent === false && <CloudUpload color="disabled" />}
            {sent === true && <CloudUpload color="success" />}
            <p>{fileName}</p>
          </Box>
        </Grid>
        <Grid item xs={5}>
          <Box className="flex flex-row space-x-2">
            <TextField
              disabled={sent}
              label="企業No(半角数字4桁)"
              defaultValue={companyNo}
              variant="outlined"
              // eslint-disable-next-line no-return-assign
              onChange={e => (e.target.value = filterNumber(e.target.value))}
              onBlur={e => {
                if (e.target.value === companyNo) {
                  return;
                }

                handleChange(key, { companyNo: e.target.value });
              }}
            />
            <TextField
              disabled={sent}
              label="事業所番号(半角数字)"
              defaultValue={officeNo}
              variant="outlined"
              // eslint-disable-next-line no-return-assign
              onChange={e => (e.target.value = filterNumber(e.target.value))}
              onBlur={e => {
                if (e.target.value === officeNo) {
                  return;
                }

                handleChange(key, { officeNo: e.target.value });
              }}
            />
          </Box>
        </Grid>
        <Grid item xs={3}>
          <Grid container>
            <Grid item xs={10}>
              <FormControl className="w-full text-sm">
                <InputLabel>サービスの種類</InputLabel>
                <Select
                  disabled={sent}
                  value={service}
                  label="サービスの種類"
                  onChange={e => {
                    handleChange(key, { service: e.target.value as Service | '' });
                  }}
                >
                  <MenuItem value={undefined}>選択してください</MenuItem>
                  <MenuItem value="RS">{labels.Service.RS}</MenuItem>
                  <MenuItem value="RY">{labels.Service.RY}</MenuItem>
                  <MenuItem value="RYSHORT">{labels.Service.RYSHORT}</MenuItem>
                  <MenuItem value="HOMECARE-RS">{labels.Service['HOMECARE-RS']}</MenuItem>
                  <MenuItem value="HOMECARE-RY">{labels.Service['HOMECARE-RY']}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={2} className="flex cursor-pointer items-center justify-center">
              <IconButton
                onClick={() => {
                  handleDelete(key);
                }}
              >
                <Delete fontSize="large" color="action" />
              </IconButton>
            </Grid>
          </Grid>
        </Grid>
        {/* eslint-disable-next-line react/prop-types */}
        {errorMessages.length > 0 && (
          <Grid item xs={12} className="py-2">
            <ErrorMessages errorMessages={errorMessages} />
          </Grid>
        )}
      </Grid>
    );
  },
  (prev, next) => deepEqual(prev, next),
);

export { UploadCsvFileProperties };
